/* @flow */
import * as F from 'lodash/fp';
import * as React from 'react';

import { Box, Flex, Footer, Space, Text } from '@sonyged/proc-ui';
import css from '@styled-system/css';
import styled from 'styled-components';

import type { Customer, SimulationState } from '../rules/types';
import { CustomerIcon } from './CustomerIcon';
import { SushiContext } from '../SushiContext';
import type { Sushi } from '../rules/types';
import SushiMenu from './SushiMenu';

type Props = {
  customers: Array<Customer>,
  lastState: SimulationState,
  firstHalfState: ?SimulationState,
  onNext: Function,
  fromOverview?: boolean,
  half: 'first' | 'second',
  roundIndex: number,
};

export function SushiResultScreen(props: Props) {
  const {
    customers,
    lastState: { eatens, belt },
    firstHalfState,
    onNext,
    fromOverview,
  } = props;

  const mapper = React.useContext(SushiContext);

  const allEatens = F.flatten(eatens);

  const score = F.pipe(F.map(mapper.getSushiPrice), F.sum)(allEatens);

  const remainingSushiFirstHalf: ?Array<Sushi> = firstHalfState
    ? firstHalfState.belt.filter(slot => slot.type === 'filled').map((x: any) => x.sushi)
    : null;
  const currentRemainingSushi: Array<Sushi> = belt.filter(slot => slot.type === 'filled').map((x: any) => x.sushi);

  let showBothHalf = false;
  let first: Array<Sushi>;
  let second: Array<Sushi>;
  if (remainingSushiFirstHalf) {
    showBothHalf = true;
    first = remainingSushiFirstHalf;
    second = currentRemainingSushi;
  } else {
    showBothHalf = false;
    first = currentRemainingSushi;
    second = [];
  }

  let menuText;
  if (props.half === 'first') {
    menuText = '組目 前半 けっか';
  }

  if (props.half === 'second') {
    menuText = '組目 けっか';
  }

  return (
    <Flex height="100%" flexDirection="column" bg="tertiary100">
      <SushiMenu
        exitLink={mapper.getExitLink()}
        query={'?skip_intro=true'}
        title={''}
        subTitle={mapper.getTitle()}
        menuText={props.roundIndex + 1 + menuText}
      />
      <Body>
        <Box flex={1} minHeight={0} style={{ overflow: 'auto' }}>
          <Space my={['12px']}>
            <Box>
              <Panel>
                <PanelHeading>
                  <Text>もらったお金</Text>
                </PanelHeading>
                <PanelContent>
                  <Flex alignItems="center">
                    <Amount>{score}</Amount>
                    <Currency>円</Currency>
                    <Note>{/* (850 - 120) */}</Note>
                  </Flex>
                  <ExtraInfo>売れたおすし {allEatens.length}さら</ExtraInfo>
                </PanelContent>
                <Line />

                <PanelContent>
                  {eatens.map((eatens, i) => (
                    <FoodContainer key={i}>
                      <Avatar customer={customers[i]} />
                      <Flex flexDirection="column">
                        <Flex>
                          {eatens.map((sushi, sushiIndex) => (
                            <FoodIcon key={sushiIndex} src={mapper.getSushiBwIcon(sushi)} alt="Sushi" />
                          ))}
                        </Flex>
                        <FoodName>{eatens.map(mapper.getSushiName).join('、')}</FoodName>
                      </Flex>
                    </FoodContainer>
                  ))}
                </PanelContent>
              </Panel>
              <Panel>
                <PanelHeading>
                  <Text>のこったおすし</Text>
                </PanelHeading>
                <PanelContent>
                  <FoodContainer>
                    {showBothHalf && (
                      <Tag>
                        <TagName>前半</TagName>
                      </Tag>
                    )}
                    <Box flex={1} minWidth={0}>
                      <FoodIconList>
                        {first.map((sushi, i) => (
                          <FoodIcon key={i} src={mapper.getSushiBwIcon(sushi)} alt="Sushi" />
                        ))}
                      </FoodIconList>
                      <Box>
                        <FoodName>{first.map(mapper.getSushiName).join('、')}</FoodName>
                      </Box>
                    </Box>
                  </FoodContainer>
                  {showBothHalf && (
                    <FoodContainer>
                      <Tag>
                        <TagName>後半</TagName>
                      </Tag>
                      <Box flex={1} minWidth={0}>
                        <FoodIconList>
                          {second.map((sushi, i) => (
                            <FoodIcon key={i} src={mapper.getSushiBwIcon(sushi)} alt="Sushi" />
                          ))}
                        </FoodIconList>
                        <Box>
                          <FoodName>{second.map(mapper.getSushiName).join('、')}</FoodName>
                        </Box>
                      </Box>
                    </FoodContainer>
                  )}
                </PanelContent>
              </Panel>
            </Box>
          </Space>
        </Box>
      </Body>

      <Box bg="white">
        <Footer variant="raised">
          <Footer.MainButton onClick={onNext} variant={fromOverview ? 'outline' : 'attention'}>
            {fromOverview ? 'もどる' : 'つぎへ'}
          </Footer.MainButton>
        </Footer>
      </Box>
    </Flex>
  );
}

const Panel = styled(Box)(
  css({
    borderStyle: 'solid',
    borderWidth: ['1px', '1px', '2px'],
    borderRadius: ['15px'],
    borderColor: 'secondary20',
    maxWidth: ['440px', '440px', '540px'],
    width: '100%',
    bg: 'white',
    mx: 'auto',
    mb: '10px',
    overflow: 'hidden',
  })
);

const PanelHeading = styled(Box)(
  css({
    px: '12px',
    py: '8px',
    bg: 'secondary20',
    color: 'gray100',
    fontSize: ['mobile.small', 'smallPC.small', 'largePC.small'],
    lineHeight: 'title',
  })
);

const PanelContent = styled(Box)(
  css({
    padding: '12px',
  })
);

const Amount = styled(Text)(
  css({
    fontSize: '28px',
    lineHeight: 1,
    marginRight: '5px',
  })
);

const Currency = styled(Text)(
  css({
    fontSize: '20px',
    lineHeight: 1,
  })
);

const Note = styled(Text)(
  css({
    fontSize: '14px',
    marginLeft: '8px',
  })
);

const ExtraInfo = styled(Text)(
  css({
    fontSize: '13px',
    color: 'secondary100',
  })
);

const Line = styled(Box)(
  css({
    height: '2px',
    bg: 'secondary20',
  })
);

const Avatar = styled(CustomerIcon)(
  css({
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    marginTop: '4px',
    marginRight: '8px',
  })
);

const FoodContainer = styled(Flex)(
  css({
    '&:not(:last-child)': {
      marginBottom: ['6px', '6px', '8px'],
    },
  })
);

const FoodIconList = styled.div(
  css({
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: 'gray100',
    '> *': {
      verticalAlign: 'middle',
    },
  })
);

const FoodIcon = styled('img')(
  css({
    display: 'inline-block',
    size: [40, 40, 48],
  })
);

const FoodName = styled(Text)(
  css({
    fontSize: '13px',
    color: 'secondary100',
    lineHeight: 1,
  })
);

const Tag = styled(Flex)(
  css({
    flexShrink: 0,
    mt: '4px',
    height: '26px',
    width: '44px',
    bg: 'gray10',
    justifyContent: 'center',
    alignItems: 'center',
  })
);

const TagName = styled(Text)(
  css({
    fontSize: '14px',
    color: 'gray100',
  })
);

const Body = styled(Flex)(
  css({
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    px: ['16px'],
    pt: ['20px', '20px', '36px'],
    bg: 'tertiary100',
  })
);
